/**
 * client/src/components/premium/section-eyebrow.tsx
 *
 * The tiny uppercase, wide-tracked label that heads sections in the setup flow
 * ("YOUR SECURITY JOURNEY", "3 STEPS TO STRENGTHEN YOUR PROTECTION"). Two forms:
 *   - default: a left-aligned label.
 *   - divided: the label centred between two hairline rules.
 */

import type { ReactNode } from "react";

export interface SectionEyebrowProps {
  children: ReactNode;
  /** Centre the label between two hairline dividers. */
  divided?: boolean;
  /** DOM id, so a following list can name this eyebrow via `aria-labelledby`. */
  id?: string;
  className?: string;
}

/**
 * The eyebrow's typography, exported so surfaces that cannot use the component
 * itself still share one definition instead of re-typing the class string.
 *
 * Two such surfaces exist today: inside a `<button>`, where this component's
 * `<p>` wrapper is invalid phrasing content (org-switcher's trigger), and where
 * the label needs `aria-hidden` because it is the sighted half of a
 * sighted+sr-only pairing (the profile-context chip). Prefer `<SectionEyebrow>`
 * whenever neither applies.
 */
export const SECTION_EYEBROW_TEXT_CLASS =
  "text-[10px] font-semibold uppercase tracking-widest text-text-tertiary";

export function SectionEyebrow({ children, divided, id, className }: SectionEyebrowProps) {
  const label = <span className={SECTION_EYEBROW_TEXT_CLASS}>{children}</span>;

  if (!divided) {
    return <p id={id} className={className}>{label}</p>;
  }

  return (
    <div id={id} className={`flex items-center gap-3 ${className ?? ""}`}>
      <div className="h-px flex-1 bg-border-default" />
      {label}
      <div className="h-px flex-1 bg-border-default" />
    </div>
  );
}
